import { Button, Tooltip } from 'antd';
import { AudioMutedOutlined, AudioOutlined, PhoneOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { BrowserPhone } from './browserPhone';
import { useElapsed } from './liveUtils';
import { LiveDot } from './LiveBits';

/** "● Browser ready" next to the Take over button while answering in the browser is selected */
export function BrowserPhoneStatusPill({ phone }: { phone: BrowserPhone }) {
  const { t } = useTranslation();
  if (phone.status === 'off') return null;
  const label =
    phone.status === 'ready'
      ? t('hotline_browserReady')
      : phone.status === 'connecting'
        ? t('hotline_browserConnecting')
        : t('hotline_browserError');
  return (
    <Tooltip title={phone.status === 'error' ? phone.error : undefined}>
      <span
        className={`browser-phone browser-phone--${phone.status}`}
        data-testid="browser-phone-status"
      >
        {phone.status === 'ready' ? <LiveDot /> : <PhoneOutlined />}
        {label}
      </span>
    </Tooltip>
  );
}

/** Floating in-call bar: who is on the line, timer, mute, hang up */
export function BrowserCallPanel({ phone }: { phone: BrowserPhone }) {
  const { t } = useTranslation();
  const elapsed = useElapsed(phone.call?.since, !!phone.call);
  if (!phone.call) return null;
  return (
    <div className="browser-call" data-testid="browser-call">
      <div className="browser-call__icon">
        <PhoneOutlined />
      </div>
      <div className="min-w-0">
        <p className="browser-call__title">{t('hotline_onTheLine')}</p>
        <p className="browser-call__who">
          {phone.call.callerNumber || phone.call.callSid || '-'}
          <span className="browser-call__time tabular-nums">{elapsed}</span>
        </p>
      </div>
      <Button
        shape="round"
        icon={phone.muted ? <AudioMutedOutlined /> : <AudioOutlined />}
        onClick={phone.toggleMute}
        data-testid="browser-call-mute"
      >
        {phone.muted ? t('hotline_unmute') : t('hotline_mute')}
      </Button>
      <Button
        shape="round"
        danger
        type="primary"
        icon={<PhoneOutlined rotate={225} />}
        onClick={phone.hangUp}
        data-testid="browser-call-hangup"
      >
        {t('hotline_hangUp')}
      </Button>
    </div>
  );
}
